// Modal re-assert primitive — the tiniest possible leaf shared by the resume
// reconciler and every dialog 'close' listener that must survive a re-assert.
//
// ISOLATION CONTRACT: this module imports NOTHING. It is the dependency floor
// of the resume-lockup fix. warmupWelcome.ts and ui/modal.ts depend on THIS
// leaf (to bail their side-effecting close listeners during a re-assert); the
// reconciler (ui/resumeReconciler.ts) depends on this leaf too. Nothing here
// depends on either of them — keeping the graph acyclic.
//
// Why it exists: on iOS Safari, backgrounding a tab and resuming can leave a
// native <dialog> visually present but with a stale top-layer / backdrop that
// swallows pointer + focus, producing a full-page lockup. The cure is to
// close() then showModal() the dialog, re-promoting it cleanly into the top
// layer. But close() dispatches a synchronous 'close' event, and our dialogs'
// real 'close' listeners run teardown (remove the dialog, fire continuation
// callbacks). During a re-assert we must NOT run that teardown. This attribute
// is the flag those listeners check to bail.

export const REASSERT_ATTR = 'data-reasserting'

// True while a re-assert (close→showModal cycle) is in flight on `dialog`. The
// dialog's own 'close' listeners read this as their first statement and return
// early when it is true, so the re-assert's close() doesn't trigger teardown.
export function isReasserting(dialog: Element): boolean {
  return dialog.hasAttribute(REASSERT_ATTR)
}

export function markReasserting(dialog: Element): void {
  dialog.setAttribute(REASSERT_ATTR, '')
}

export function unmarkReasserting(dialog: Element): void {
  dialog.removeAttribute(REASSERT_ATTR)
}
